/**
 * OZK 닉네임 생성 및 검증 모듈 (V5)
 *
 * 핵심 원칙:
 * - `지역 + 형용사 + 호주 동물` 형식
 * - 최대 12자 제한 (UI 안정성)
 * - 한글만 사용
 * - 호주 동물 이름만 사용
 * - 중복 시 #숫자 태그 자동 추가
 */

// ================================================================
// 단어 풀 정의
// ================================================================

// 지역명 풀
export const CITIES = [
  "시드니",
  "멜번",
  "브리즈번",
  "골드코스트",
  "퍼스",
  "애들레이드",
  "캔버라",
  "호바트",
  "다윈",
  "에핑",
  "이스트우드",
  "스트라스필드",
  "챗스우드",
  "파라마타",
  "본다이",
];

// 형용사 풀: 짧고 긍정적이며 사람을 평가하거나 과장하지 않는 표현
export const ADJECTIVES = [
  "깜찍",
  "다정",
  "명랑",
  "씩씩",
  "발랄",
  "포근",
  "유쾌",
  "차분",
  "상냥",
  "든든",
  "산뜻",
  "재치",
  "반짝",
  "소중",
  "멋진",
];

// 호주 대표 동물
export const ANIMALS = [
  "코알라",
  "캥거루",
  "쿼카",
  "웜뱃",
  "딩고",
  "에뮤",
  "왈라비",
  "빌비",
  "포섬",
  "로리킷",
  "코카투",
  "쿠카버라",
  "펭귄",
];

// ================================================================
// 정규식 및 상수
// ================================================================

// 기본 한글 검증 (#숫자 태그 허용)
const HANGUL_REGEX = /^[가-힣\s#0-9]+$/;

// V5: [지역] [형용사] [호주 동물]
const NICKNAME_PATTERN = /^[가-힣]{2,6}\s[가-힣]{2,3}\s[가-힣]{2,4}$/;

// 최대 길이 제한
export const MAX_NICKNAME_LENGTH = 12;

// 금지 단어 리스트 (예시, 실제로는 서버에서 관리)
const BANNED_WORDS: string[] = [
  // TODO: 실제 서비스용 금지어 리스트로 교체
  "욕설예시",
  "비속어예시",
];

// ================================================================
// 도시명 영어→한글 변환 매핑
// ================================================================

/**
 * 영어 도시명을 한글로 변환하는 매핑
 * 주요 호주 도시 및 교민 지역
 */
const CITY_NAME_MAP: Record<string, string> = {
  // 주 코드가 전달되면 대표 도시로 변환
  NSW: "시드니",
  VIC: "멜번",
  QLD: "브리즈번",
  WA: "퍼스",
  SA: "애들레이드",
  ACT: "캔버라",
  TAS: "호바트",
  NT: "다윈",
  // 주요 도시
  Sydney: "시드니",
  sydney: "시드니",
  Melbourne: "멜번",
  melbourne: "멜번",
  Brisbane: "브리즈번",
  brisbane: "브리즈번",
  Perth: "퍼스",
  perth: "퍼스",
  Adelaide: "애들레이드",
  adelaide: "애들레이드",
  Canberra: "캔버라",
  canberra: "캔버라",
  Hobart: "호바트",
  hobart: "호바트",
  Darwin: "다윈",
  darwin: "다윈",
  // NSW 주요 교민 지역
  Epping: "에핑",
  epping: "에핑",
  Eastwood: "이스트우드",
  eastwood: "이스트우드",
  Strathfield: "스트라스필드",
  strathfield: "스트라스필드",
  Chatswood: "챗스우드",
  chatswood: "챗스우드",
  Parramatta: "파라마타",
  parramatta: "파라마타",
  Burwood: "버우드",
  burwood: "버우드",
  Lidcombe: "리드컴",
  lidcombe: "리드컴",
  Campsie: "캠시",
  campsie: "캠시",
  Rhodes: "로즈",
  rhodes: "로즈",
  Bondi: "본다이",
  bondi: "본다이",
  // VIC 주요 교민 지역
  "Glen Waverley": "글렌웨이벌리",
  "glen waverley": "글렌웨이벌리",
  "Box Hill": "박스힐",
  "box hill": "박스힐",
  Doncaster: "돈캐스터",
  doncaster: "돈캐스터",
  Clayton: "클레이튼",
  clayton: "클레이튼",
  Bundoora: "번두라",
  bundoora: "번두라",
  // QLD 주요 교민 지역
  Sunnybank: "써니뱅크",
  sunnybank: "써니뱅크",
  "Upper Mount Gravatt": "어퍼그래밧",
  "upper mount gravatt": "어퍼그래밧",
  Toowong: "투웡",
  toowong: "투웡",
  Southport: "사우스포트",
  southport: "사우스포트",
  // SA 주요 교민 지역
  Norwood: "노우드",
  norwood: "노우드",
  Prospect: "프로스펙트",
  prospect: "프로스펙트",
  // WA 주요 교민 지역
  "Canning Vale": "캐닝베일",
  "canning vale": "캐닝베일",
  Willetton: "윌레튼",
  willetton: "윌레튼",
  // ACT
  Belconnen: "벨코넨",
  belconnen: "벨코넨",
  // 기타 주요 도시
  "Gold Coast": "골드코스트",
  "gold coast": "골드코스트",
  Newcastle: "뉴캐슬",
  newcastle: "뉴캐슬",
  Wollongong: "울릉공",
  wollongong: "울릉공",
  Geelong: "질롱",
  geelong: "질롱",
  Cairns: "케언스",
  cairns: "케언스",
};

/**
 * 영어 도시명을 한글로 변환
 * @param cityName 영어 도시명 (예: "Epping", "Sydney")
 * @returns 한글 도시명 (예: "에핑", "시드니"), 변환 불가능하면 원본 반환
 */
function translateCityNameToKorean(cityName: string): string {
  if (!cityName) return cityName;

  const trimmed = cityName.trim();

  // 이미 한글인지 확인 (한글 유니코드 범위)
  if (/^[가-힣\s]+$/.test(trimmed)) {
    return trimmed;
  }

  // 매핑에서 찾기
  if (CITY_NAME_MAP[trimmed]) {
    return CITY_NAME_MAP[trimmed];
  }

  // 대소문자 무시하고 찾기
  const lowerTrimmed = trimmed.toLowerCase();
  for (const [key, value] of Object.entries(CITY_NAME_MAP)) {
    if (key.toLowerCase() === lowerTrimmed) {
      return value;
    }
  }

  // 변환 불가능하면 원본 반환
  return trimmed;
}

// ================================================================
// 유틸 함수
// ================================================================

/**
 * 공백 정규화
 */
export function normalizeWhitespace(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

/**
 * 금지어 포함 여부 확인
 */
export function containsBannedWord(text: string): boolean {
  const normalized = normalizeWhitespace(text);
  return BANNED_WORDS.some((bad) => bad && normalized.includes(bad));
}

/**
 * 닉네임 형식 검증
 */
export function isValidNicknameFormat(nickname: string): boolean {
  const normalized = normalizeWhitespace(nickname);

  // #숫자 태그 제거 후 검증
  const baseNickname = normalized.replace(/\s*#\d+$/, "");

  if (!HANGUL_REGEX.test(baseNickname)) return false;
  if (baseNickname.length > MAX_NICKNAME_LENGTH) return false;
  if (containsBannedWord(baseNickname)) return false;

  if (!NICKNAME_PATTERN.test(baseNickname)) return false;

  const [, adjective, animal] = baseNickname.split(" ");
  return ADJECTIVES.includes(adjective) && ANIMALS.includes(animal);
}

/**
 * 생물명 축약 (12자 제한을 위해)
 */
export function shortenCreature(creature: string): string {
  return creature.substring(0, 4);
}

/**
 * #숫자 태그 제거 (UI 표시용)
 */
export function extractDisplayNickname(nickname: string): string {
  return nickname.replace(/\s*#\d+$/, "");
}

// ================================================================
// 닉네임 생성 함수
// ================================================================

export interface GenerateNicknameOptions {
  suburbLabel?: string; // "시드니", "에핑" 등 한글 표기 지역명 (옵션)
  preferShort?: boolean; // 이전 호출부 호환용 (V5에서는 사용하지 않음)
  maxRetries?: number; // 중복·금지어 피하기 위한 재시도 횟수
}

/**
 * 긴 지역명은 UI 12자 제한 안에서 대표 도시로 정규화
 */
function normalizeNicknameLocation(suburbLabel?: string): string {
  if (!suburbLabel) return "호주";
  const translated = translateCityNameToKorean(suburbLabel);
  if (/^[가-힣]{2,6}$/.test(translated)) return translated;
  return "호주";
}

/**
 * V5 닉네임 한 개 생성
 */
function generateStructuredNickname(location: string): string {
  const adjective = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const fittingAnimals = ANIMALS.filter(
    (animal) => `${location} ${adjective} ${animal}`.length <= MAX_NICKNAME_LENGTH,
  );
  const animalPool = fittingAnimals.length > 0 ? fittingAnimals : ["에뮤"];
  const animal = animalPool[Math.floor(Math.random() * animalPool.length)];
  return `${location} ${adjective} ${animal}`;
}

/**
 * 자동 닉네임 생성
 */
export function generateNickname(
  options: GenerateNicknameOptions = {}
): string {
  const { suburbLabel, maxRetries = 20 } = options;
  const location = normalizeNicknameLocation(suburbLabel);

  for (let i = 0; i < maxRetries; i++) {
    const candidate = generateStructuredNickname(location);

    // 검증
    if (
      candidate.length <= MAX_NICKNAME_LENGTH &&
      isValidNicknameFormat(candidate)
    ) {
      return candidate;
    }
  }

  // Fallback
  return "호주 다정 코알라";
}

/**
 * 닉네임 후보 생성 (8개 추천)
 */
export function generateNicknameSuggestions(suburbLabel?: string): string[] {
  const location = normalizeNicknameLocation(suburbLabel);
  const candidates = ADJECTIVES.flatMap((adjective) =>
    ANIMALS.map((animal) => `${location} ${adjective} ${animal}`),
  ).filter(isValidNicknameFormat);

  // Fisher-Yates shuffle: 중복 없이 항상 8개를 제공한다.
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  return candidates.slice(0, 8);
}

// ================================================================
// 중복 처리 함수
// ================================================================

/**
 * DB에서 중복 확인 (기본 닉네임만, #숫자 제외)
 */
/**
 * 닉네임 중복 확인
 *
 * ⚠️ 중요: DB 에러 발생 시 false를 반환하여 닉네임 설정을 막지 않도록 처리
 * 에러를 true로 처리하면 모든 닉네임이 중복으로 판정되어 사용자가 닉네임을 설정할 수 없게 됨
 */
export async function isNicknameTaken(
  db: {
    prepare: (query: string) => {
      bind: (...args: any[]) => { first: () => Promise<any> };
    };
  },
  nickname: string
): Promise<boolean> {
  // 빈 닉네임은 사용 불가 (하지만 false 반환하여 에러가 아님을 명시)
  if (!nickname || !nickname.trim()) {
    return false;
  }

  const trimmed = nickname.trim();

  try {
    // 완전 일치(=) 비교만 사용
    // LIKE 패턴 제거 - 부분 일치로 인한 과도한 중복 판정 방지
    // 정확히 같은 닉네임만 중복으로 판정
    const row = await db
      .prepare(
        `SELECT 1 AS exists
         FROM users
         WHERE nickname = ? OR nickname_display = ?
         LIMIT 1`
      )
      .bind(trimmed, trimmed)
      .first();

    return !!row; // row가 있으면 true (중복), 없으면 false (사용 가능)
  } catch (error: any) {
    // ❗ 핵심: DB 에러 발생 시 false를 반환하여 닉네임 설정을 막지 않음
    // 에러를 true로 처리하면 모든 닉네임이 중복으로 판정되어
    // generateUniqueNickname()이 100번 모두 실패하고 500 에러 발생
    console.error(
      "[isNicknameTaken] DB error, but NOT blocking nickname:",
      error
    );
    console.error("[isNicknameTaken] Nickname:", nickname);
    console.error(
      "[isNicknameTaken] Error details:",
      error?.message,
      error?.stack
    );
    // 에러 발생 시 false 반환 → 최소한 사용자는 닉네임을 한 번이라도 설정할 수 있게
    return false;
  }
}

/**
 * 중복 시 #숫자 태그 추가하여 고유 닉네임 생성
 */
export async function generateUniqueNickname(
  db: {
    prepare: (query: string) => {
      bind: (...args: any[]) => { first: () => Promise<any> };
    };
  },
  baseNickname: string
): Promise<string> {
  try {
    const trimmed = baseNickname.trim();

    if (!trimmed || trimmed.length === 0) {
      throw new Error("닉네임이 비어있습니다");
    }

    // 1. 기본 닉네임으로 중복 확인 (완전 일치만 확인)
    const exists = await isNicknameTaken(db, trimmed);

    if (!exists) {
      return trimmed;
    }

    // 2. #숫자 태그 추가하여 재시도
    // 완전 일치 비교이므로 최대 100개 정도면 충분 (999는 너무 많음)
    let counter = 1;
    let candidate = `${trimmed} #${counter}`;

    while (await isNicknameTaken(db, candidate)) {
      counter++;
      candidate = `${trimmed} #${counter}`;

      // 최대 100까지 시도 (완전 일치이므로 100개면 충분)
      if (counter > 100) {
        throw new Error(
          "닉네임 생성 실패: 사용 가능한 닉네임을 찾을 수 없습니다"
        );
      }
    }

    return candidate;
  } catch (error: any) {
    console.error("[generateUniqueNickname] Error:", error);
    console.error("[generateUniqueNickname] Base nickname:", baseNickname);
    console.error(
      "[generateUniqueNickname] Error details:",
      error?.message,
      error?.stack
    );
    throw error; // 에러를 다시 throw하여 상위에서 처리
  }
}
